import { useSyncExternalStore } from 'react';
import { beginFinderClick, finderCommand, finderConnected } from './finderRelay';

export type FolderMode = 'auto' | 'windows' | 'finder' | 'browser';
const eventName = 'skrepka-folder-mode';
const key = (id: number) => `skrepka-folder-mode:${id}`;
export const isWindows = () => /Windows/i.test(navigator.userAgent) || /^Win/i.test(navigator.platform);
export const isMac = () => /Mac/i.test(navigator.platform || navigator.userAgent) && !/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && !(navigator.maxTouchPoints > 1);
export const nativeFolderName = () => isMac() ? 'Finder' : 'Windows';
export const supportsNativeFolders = () => isWindows() || isMac();
export function readFolderMode(id: number): FolderMode {
  try { const value = localStorage.getItem(key(id)); return value === 'browser' || (value === 'windows' && isWindows()) || (value === 'finder' && isMac()) ? value : 'auto'; } catch { return 'auto'; }
}
export function setFolderMode(id: number, value: FolderMode) {
  try { localStorage.setItem(key(id), value); } catch { /* A private browser can disallow storage. */ }
  window.dispatchEvent(new Event(eventName));
}
export function useFolderMode(id: number) {
  return useSyncExternalStore(listener => {
    window.addEventListener('storage', listener); window.addEventListener(eventName, listener);
    return () => { window.removeEventListener('storage', listener); window.removeEventListener(eventName, listener); };
  }, () => readFolderMode(id));
}

let helperReadyUntil = 0;
export function beginFolderClick(activate = (uri: string) => { window.location.href = uri; }): string | undefined {
  if (isMac()) return finderConnected() ? beginFinderClick(activate) : undefined;
  if (Date.now() >= helperReadyUntil) return;
  const nonce = crypto.randomUUID().replaceAll('-', '');
  activate(`skrepka-folders://open/${nonce}`);
  return nonce;
}
export function folderHelperStatus(): Promise<'ready' | 'update' | 'unavailable'> {
  if (isMac()) return Promise.resolve(finderConnected() ? 'ready' : 'unavailable');
  return new Promise(resolve => {
    let socket: WebSocket | undefined, done = false;
    const finish = (value: 'ready' | 'update' | 'unavailable') => { if (done) return; done = true; helperReadyUntil = value === 'ready' ? Date.now() + 10000 : 0; clearTimeout(timer); socket?.close(); resolve(value); };
    const timer = setTimeout(() => finish('unavailable'), 1200);
    try {
      socket = new WebSocket('ws://127.0.0.1:17844/status');
      socket.onmessage = event => { try { const p = JSON.parse(String(event.data)) as Record<string, unknown>; finish(p.type === 'folder-helper-status' ? p.protocol === 1 && p.folders === true && p.activationOnClick === true ? 'ready' : 'update' : 'unavailable'); } catch { finish('unavailable'); } };
      socket.onerror = socket.onclose = () => finish('unavailable');
    } catch { finish('unavailable'); }
  });
}
export async function folderHelperReady(): Promise<boolean> { return await folderHelperStatus() === 'ready'; }
export function openHelperFolder(ticket: string, signal: AbortSignal | undefined, nonce: string): Promise<boolean> {
  if (isMac()) return finderCommand(nonce, signal, ticket);
  return new Promise(resolve => {
    let socket: WebSocket | undefined, done = false, retryWithoutActivation = false;
    const finish = (value: boolean) => { if (done) return; done = true; clearTimeout(timer); signal?.removeEventListener('abort', cancel); socket?.close(); resolve(value); };
    const cancel = () => finish(false);
    const timer = setTimeout(() => finish(false), 24000);
    if (signal?.aborted || !/^[a-f0-9]{32}$/.test(nonce)) { finish(false); return; }
    signal?.addEventListener('abort', cancel, { once: true });
    const connect = (foreground: boolean) => {
      const current = new WebSocket('ws://127.0.0.1:17844/folders');
      socket = current;
      current.onopen = () => { if (!done && socket === current) current.send(JSON.stringify({ type: 'open-folder', ticket, ...(foreground ? { foreground: nonce } : {}) })); };
      current.onmessage = event => {
        if (done || socket !== current) return;
        try {
          const p = JSON.parse(String(event.data)) as Record<string, unknown>;
          // The helper has not consumed the ticket when activation times out.
          // Retry only that explicit result; never repeat an uncertain open.
          if (p.type === 'folder-result' && p.opened === false && p.reason === 'activation' && !retryWithoutActivation) {
            retryWithoutActivation = true;
            socket = undefined;
            current.close();
            if (!done && !signal?.aborted) connect(false);
            return;
          }
          finish(p.type === 'folder-result' && p.opened === true);
        } catch { finish(false); }
      };
      current.onerror = current.onclose = () => { if (socket === current) finish(false); };
    };
    try { connect(true); } catch { finish(false); }
  });
}
